import { createSlice } from "@reduxjs/toolkit";
import { createSupportTicket, testJiraConnection, fetchUserTickets } from "../thunks/helpDeskThunk";

const helpDeskSlice = createSlice({
  name: "helpDesk",
  initialState: {
    // Modal state
    isModalOpen: false,
    
    // Ticket creation state
    status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
    
    // Last created ticket info
    lastTicket: null,
    
    // JIRA connection state
    connectionStatus: "unknown", // 'unknown' | 'testing' | 'connected' | 'failed'
    connectionError: null,
    jiraConfig: null,
    
    // Console logs for ticket submission
    consoleLogs: null,

    // User tickets state
    userTickets: [],
    ticketsStatus: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
    ticketsError: null,
  },
  reducers: {
    // Modal controls
    openModal: (state) => {
      state.isModalOpen = true;
      state.error = null;
    },
    closeModal: (state) => {
      state.isModalOpen = false;
      state.error = null;
      state.status = "idle";
      state.consoleLogs = null;
    },
    
    // Console logs
    setConsoleLogs: (state, action) => {
      state.consoleLogs = action.payload;
    },
    clearConsoleLogs: (state) => {
      state.consoleLogs = null;
    },
    
    // Error handling
    clearError: (state) => {
      state.error = null;
    },
    clearConnectionError: (state) => {
      state.connectionError = null;
    },

    // User tickets handling
    clearTicketsError: (state) => {
      state.ticketsError = null;
    },
    
    // Reset state
    resetHelpDesk: (state) => {
      state.isModalOpen = false;
      state.status = "idle";
      state.error = null;
      state.lastTicket = null;
      state.consoleLogs = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create support ticket cases
      .addCase(createSupportTicket.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(createSupportTicket.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.lastTicket = action.payload.data;
        state.isModalOpen = false; // Close modal on success
        state.consoleLogs = null;
      })
      .addCase(createSupportTicket.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to create support ticket";
      })
      
      // Test JIRA connection cases
      .addCase(testJiraConnection.pending, (state) => {
        state.connectionStatus = "testing";
        state.connectionError = null;
      })
      .addCase(testJiraConnection.fulfilled, (state, action) => {
        state.connectionStatus = "connected";
        state.jiraConfig = action.payload.data;
      })
      .addCase(testJiraConnection.rejected, (state, action) => {
        state.connectionStatus = "failed";
        state.connectionError = action.payload || "Failed to connect to JIRA";
      })

      // Fetch user tickets cases
      .addCase(fetchUserTickets.pending, (state) => {
        state.ticketsStatus = "loading";
        state.ticketsError = null;
      })
      .addCase(fetchUserTickets.fulfilled, (state, action) => {
        state.ticketsStatus = "succeeded";
        state.userTickets = action.payload.data.tickets || [];
      })
      .addCase(fetchUserTickets.rejected, (state, action) => {
        state.ticketsStatus = "failed";
        state.ticketsError = action.payload || "Failed to fetch user tickets";
      });
  },
});

export const {
  openModal,
  closeModal,
  setConsoleLogs,
  clearConsoleLogs,
  clearError,
  clearConnectionError,
  clearTicketsError,
  resetHelpDesk,
} = helpDeskSlice.actions;

export default helpDeskSlice.reducer;